import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogHttpResponse } from "../contracts/api/BacklogHttpResponse";
import type { BacklogRequest as BacklogRequestContract } from "../contracts/api/BacklogRequest";
import type {
	BacklogRequestBody,
	BacklogRequestOption,
	BacklogRequestOptions,
	BacklogRequestOptionsGet,
} from "../contracts/api/BacklogRequestOption";
import type { BacklogRequestResolver } from "../contracts/api/BacklogRequestResolver";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { BacklogResponseNoContent } from "../contracts/api/BacklogResponseNoContent";
import type { BacklogHttpFetcher } from "../contracts/utils/BacklogHttpFetcher";
import { BacklogApiAbortError } from "../errors/BacklogApiAbortError";
import { BacklogApiAuthenticationError } from "../errors/BacklogApiAuthenticationError";
import { BacklogApiAuthenticationInvalidTokenError } from "../errors/BacklogApiAuthenticationInvalidTokenError";
import { BacklogApiAuthenticationTokenExpiredError } from "../errors/BacklogApiAuthenticationTokenExpiredError";
import { BacklogApiError } from "../errors/BacklogApiError";
import type { BacklogApiErrorDetailList } from "../errors/BacklogApiErrorDetailList";
import { BacklogApiTooManyRequestsError } from "../errors/BacklogApiTooManyRequestsError";
import { BacklogApiUnexpectedError } from "../errors/BacklogApiUnexpectedError";
import type { RequestMethod } from "../types/RequestMethod";
import {
	assert,
	cloneHeader,
	resolveBacklogContainer,
} from "../utils/BacklogUtil";
import { BacklogResponse as BacklogResponseImpl } from "./BacklogResponse";
import { BacklogResponseFile as BacklogResponseFileImpl } from "./BacklogResponseFile";
import { BacklogResponseNoContent as BacklogResponseNoContentImpl } from "./BacklogResponseNoContent";

export class BacklogRequest implements BacklogRequestContract {
	private request_resolver: BacklogRequestResolver;
	private fetcher: BacklogHttpFetcher;
	constructor(
		private options: {
			config: BacklogConfig;
			request_resolver?: BacklogRequestResolver;
			fetcher?: BacklogHttpFetcher;
		},
	) {
		assert(this.options);
		this.request_resolver =
			this.options.request_resolver ??
			resolveBacklogContainer("BacklogRequestResolver", {
				config: this.options.config,
			});

		assert(this.request_resolver);

		this.fetcher =
			this.options.fetcher ?? resolveBacklogContainer("BacklogHttpFetcher");

		assert(this.fetcher);
	}

	async get<T extends object>(
		options: BacklogRequestOptionsGet<T>,
	): Promise<BacklogResponse<T>> {
		const {
			response,
			url,
			options: opt,
		} = await this.request({
			method: "GET",
			path: options.path,
			query: options.query,
			context: options.context,
		});

		const t = await this.convertJson<T>(response);

		return new BacklogResponseImpl<T>({
			...this.createInternal("GET", options),
			...{
				request: { url, options: opt },
				response,
				data: t,
			},
		});
	}

	private async convertJson<T extends object>(
		res: BacklogHttpResponse,
	): Promise<T> {
		const content_type = res.getContentType();
		if (content_type.mediaType !== "application/json") {
			throw new BacklogApiUnexpectedError({ response: res });
		}
		return (await res.json()) as T;
	}

	async getFile(
		options: BacklogRequestOptionsGet<object>,
	): Promise<BacklogResponse<BacklogResponseFile>> {
		const {
			response,
			url,
			options: opt,
		} = await this.request({
			method: "GET",
			path: options.path,
			context: options.context,
		});

		const f = new BacklogResponseFileImpl({
			response: response,
		});

		return new BacklogResponseImpl<BacklogResponseFile>({
			...this.createInternal("GET", options),
			...{
				request: { url, options: opt },
				response,
				data: f,
			},
		});
	}

	async post<T extends object>(
		options: BacklogRequestOptions<T>,
	): Promise<BacklogResponse<T>> {
		const {
			response,
			url,
			options: opt,
		} = await this.request({
			method: "POST",
			path: options.path,
			query: options.query,
			body: options.body,
			context: options.context,
		});

		const t = await this.convertJson<T>(response);

		return new BacklogResponseImpl<T>({
			...this.createInternal("POST", options),
			...{
				request: { url, options: opt },
				response,
				data: t,
			},
		});
	}

	async postNoContent(
		options: BacklogRequestOptions<object>,
	): Promise<BacklogResponse<BacklogResponseNoContent>> {
		const {
			response,
			url,
			options: opt,
		} = await this.request({
			method: "POST",
			path: options.path,
			query: options.query,
			body: options.body,
			context: options.context,
		});

		return new BacklogResponseImpl<BacklogResponseNoContent>({
			...this.createInternal("POST", options),
			...{
				request: { url, options: opt },
				response,
				data: new BacklogResponseNoContentImpl({ response }),
			},
		});
	}

	async put<T extends object>(
		options: BacklogRequestOptions<T>,
	): Promise<BacklogResponse<T>> {
		const {
			response,
			url,
			options: opt,
		} = await this.request({
			method: "PUT",
			path: options.path,
			query: options.query,
			body: options.body,
			context: options.context,
		});

		const t = await this.convertJson<T>(response);

		return new BacklogResponseImpl<T>({
			...this.createInternal("PUT", options),
			...{
				request: { url, options: opt },
				response,
				data: t,
			},
		});
	}

	async patch<T extends object>(
		options: BacklogRequestOptions<T>,
	): Promise<BacklogResponse<T>> {
		const {
			response,
			url,
			options: opt,
		} = await this.request({
			method: "PATCH",
			path: options.path,
			query: options.query,
			body: options.body,
			context: options.context,
		});

		const t = await this.convertJson<T>(response);

		return new BacklogResponseImpl<T>({
			...this.createInternal("PATCH", options),
			...{
				request: { url, options: opt },
				response,
				data: t,
			},
		});
	}

	async delete<T extends object>(
		options: BacklogRequestOptions<T>,
	): Promise<BacklogResponse<T>> {
		const {
			response,
			url,
			options: opt,
		} = await this.request({
			method: "DELETE",
			path: options.path,
			query: options.query,
			body: options.body,
			context: options.context,
		});

		const t = await this.convertJson<T>(response);

		return new BacklogResponseImpl<T>({
			...this.createInternal("DELETE", options),
			...{
				request: { url, options: opt },
				response,
				data: t,
			},
		});
	}

	private async request(args: {
		method: string;
		path: string;
		query?: URLSearchParams;
		body?: BacklogRequestBody;
		context?: BacklogApiContext;
	}): Promise<{
		response: BacklogHttpResponse;
		url: URL;
		options: BacklogRequestOption;
	}> {
		const context_request_option = args?.context?.requestOptions ?? {};

		const base_options: BacklogRequestOption = {
			...(this.options.config.getDefaultRequestOptions() ?? {}),
			...context_request_option,
			// force overwrites
			...{
				method: args.method,
				body: args.body,
			},
			// clone Headers object
			...{
				headers: this.createHeader(context_request_option.headers),
			},
		};

		const { url, options } = this.request_resolver.resolve({
			path: args.path,
			options: base_options,
			query: args.query,
			body: args.body,
		});

		try {
			const response = await this.requestFetch(url, options);
			return { response, url, options };
		} catch (e: unknown) {
			this.throwError(e, url, options);
		}
	}

	private createHeader(
		context_request_option_headers: Headers | undefined,
	): Headers {
		if (context_request_option_headers) {
			return cloneHeader(context_request_option_headers);
		}
		const default_request_options =
			this.options.config.getDefaultRequestOptions() ?? {};
		if (default_request_options.headers) {
			return cloneHeader(default_request_options.headers);
		}
		return new Headers();
	}

	private async requestFetch(
		url: URL,
		options: BacklogRequestOption,
	): Promise<BacklogHttpResponse> {
		const fetch_response = this.fetch(url, options);

		const response = await fetch_response;

		await this.checkStatus(response);

		return response;
	}

	private async fetch(
		url: URL,
		options: BacklogRequestOption,
	): Promise<BacklogHttpResponse> {
		const res = this.fetcher.fetch(url, options);
		return res;
	}

	private async checkStatus(res: BacklogHttpResponse): Promise<void> {
		if (res.ok) {
			return;
		}

		const content_type = res.getContentType();

		if (content_type.mediaType !== "application/json") {
			throw new BacklogApiUnexpectedError({ response: res });
		}

		const data = await res.json();

		if (!(typeof data === "object")) {
			throw new BacklogApiUnexpectedError({ response: res });
		}

		if (data == null) {
			throw new BacklogApiUnexpectedError({ response: res });
		}

		if (!("errors" in data)) {
			throw new BacklogApiUnexpectedError({ response: res });
		}

		if (!Array.isArray(data.errors)) {
			throw new BacklogApiUnexpectedError({ response: res });
		}

		if (data.errors.length < 1) {
			throw new BacklogApiUnexpectedError({ response: res });
		}

		const error_list = data.errors as BacklogApiErrorDetailList;

		if (res.getStatus() === 401) {
			this.throwAuthenticationError(res, error_list);
		}

		if (res.getStatus() === 429) {
			throw new BacklogApiTooManyRequestsError({
				response: res,
				error_details: error_list,
			});
		}

		throw new BacklogApiError({
			response: res,
			error_details: error_list,
		});
	}

	private throwError(
		error: unknown,
		url: string | URL,
		options: BacklogRequestOption,
	): never {
		if (!(error instanceof Error)) {
			throw error;
		}

		if (options.signal) {
			if (error === options.signal.reason) {
				throw new BacklogApiAbortError({ url, options });
			}
		}

		throw error;
	}

	private throwAuthenticationError(
		res: BacklogHttpResponse,
		error_list: BacklogApiErrorDetailList,
	): never {
		const { scheme, params } = res.getWWWAuthenticate();

		if (
			scheme.toLocaleLowerCase() === "bearer" &&
			params.get("error")?.toLocaleLowerCase() === "invalid_token"
		) {
			const error_description = (
				params.get("error_description") || ""
			).toLocaleLowerCase();
			if (error_description.includes("expired")) {
				throw new BacklogApiAuthenticationTokenExpiredError({
					response: res,
					error_details: error_list,
				});
			}
			if (error_description.includes("invalid")) {
				throw new BacklogApiAuthenticationInvalidTokenError({
					response: res,
					error_details: error_list,
				});
			}
		}

		throw new BacklogApiAuthenticationError({
			response: res,
			error_details: error_list,
		});
	}

	private createInternal<T extends object>(
		method: RequestMethod,
		options: BacklogRequestOptionsGet<T>,
	): {
		spec: {
			endpoint: string;
			method: RequestMethod;
		};
	} {
		return {
			spec: {
				endpoint: options.internal.endpoint,
				method: method,
			},
		};
	}
}
